import React, { useState, useRef, useEffect } from 'react';
import './MultiSelect.css';

/**
 * Custom Multi-Select Component with Checkboxes
 * Works exactly like SearchableSelect but supports multiple selections
 */
const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  name,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Ensure value is always an array
  const selectedValues = Array.isArray(value) ? value : [];

  // Normalize options to {value, label} format
  const normalizedOptions = options.map(opt => {
    if (typeof opt === 'string') {
      return { value: opt, label: opt };
    }
    if (opt && typeof opt === 'object') {
      return {
        value: opt.value || opt.id || opt.label || String(opt),
        label: opt.label || opt.name || opt.value || String(opt)
      };
    }
    return { value: String(opt), label: String(opt) };
  });

  // Filter options based on search
  const filteredOptions = searchText
    ? normalizedOptions.filter(opt =>
        opt.label.toLowerCase().includes(searchText.toLowerCase())
      )
    : normalizedOptions;

  // Get selected labels for display
  const getSelectedLabels = () => {
    return selectedValues.map(val => {
      const option = normalizedOptions.find(opt => opt.value === val);
      return option ? option.label : val;
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleSearchClick = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleOptionToggle = (optionValue) => {
    let newValue;
    if (selectedValues.includes(optionValue)) {
      // Remove option
      newValue = selectedValues.filter(v => v !== optionValue);
    } else {
      // Add option
      newValue = [...selectedValues, optionValue];
    }
    onChange(newValue);

    // Clear search text after selection to show all options again
    setSearchText('');
  };

  const selectedLabels = getSelectedLabels();

  const handleRemoveTag = (e, valueToRemove) => {
    e.stopPropagation();
    const newValue = selectedValues.filter(v => v !== valueToRemove);
    onChange(newValue);
  };

  return (
    <div
      ref={containerRef}
      className={`multi-select ${className}`}
    >
      {/* Select Control - always show chips when items are selected */}
      <div
        className={`multi-select-control ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
      >
        <div className="multi-select-value" onClick={(e) => {
          // If clicking on tags area while open, don't toggle (keep it open)
          if (isOpen && selectedValues.length > 0) {
            e.stopPropagation();
          }
        }}>
          {/* Always show chips if items are selected */}
          {selectedValues.length > 0 && (
            <div className="multi-select-tags">
              {selectedLabels.map((label, index) => (
                <span key={selectedValues[index]} className="multi-select-tag">
                  {label}
                  <button
                    type="button"
                    className="multi-select-tag-remove"
                    onClick={(e) => handleRemoveTag(e, selectedValues[index])}
                    aria-label={`Remove ${label}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Show search input when open */}
          {isOpen && (
            <input
              ref={searchInputRef}
              type="text"
              className="multi-select-search-input"
              placeholder="Type to search..."
              value={searchText}
              onChange={handleSearchChange}
              onClick={handleSearchClick}
              onFocus={() => setIsOpen(true)}
            />
          )}

          {/* Show placeholder text when closed and no selections */}
          {!isOpen && selectedValues.length === 0 && (
            <span className="multi-select-placeholder">{placeholder}</span>
          )}
        </div>

        {/* Arrow Icon */}
        <div className="multi-select-arrow">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
          </svg>
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="multi-select-menu">
          {/* Options List with Checkboxes */}
          <div className="multi-select-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <label
                  key={option.value}
                  className="multi-select-option"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => handleOptionToggle(option.value)}
                  />
                  <span className="multi-select-option-label">{option.label}</span>
                </label>
              ))
            ) : (
              <div className="multi-select-no-options">No options found</div>
            )}
          </div>

          {/* Selected Count */}
          {selectedValues.length > 0 && (
            <div className="multi-select-footer">
              {selectedValues.length} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
